import React, { useRef, useState } from 'react';

import { telemetryClickHandler } from '../telemetryHandler';
import type { DesktopNavItemConfig } from '../types';
import LayeredMenu from './LayeredMenu';
import SingleColumnMenu from './SingleColumnMenu';
import TwoColumnMenu from './TwoColumnMenu';

const AI_GRADIENT_BG = 'linear-gradient(to right, #9952E0 0%, #FF66E6 100%)';

interface DesktopNavItemProps {
  config: DesktopNavItemConfig;
}

export default function DesktopNavItem({
  config,
}: DesktopNavItemProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const isClosingRef = useRef(false);
  const { dropdown, hasGradientText, gradientIcon, stockblocksGradientIcon } =
    config;
  const resolvedGradientIcon = stockblocksGradientIcon || gradientIcon;

  // Plain link — no dropdown
  if (!dropdown) {
    return (
      <a
        href={config.href}
        className="flex h-10 items-center gap-1 rounded-xl px-3 text-base font-semibold text-gray-900 hover:bg-blue-100 hover:text-blue-600 no-underline"
        onClick={telemetryClickHandler(
          config.telemetrySlug,
          config.telemetryEvents
        )}
      >
        {config.label}
      </a>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={(): void => setIsOpen(true)}
      onMouseLeave={(): void => setIsOpen(false)}
      onFocus={(): void => {
        if (!isClosingRef.current) {
          setIsOpen(true);
        }
        isClosingRef.current = false;
      }}
      onBlur={(e: React.FocusEvent<HTMLDivElement>): void => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsOpen(false);
        }
      }}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>): void => {
        if (e.key === 'Escape') {
          isClosingRef.current = true;
          setIsOpen(false);
          const button = e.currentTarget.querySelector('button');
          button?.focus();
        }
      }}
    >
      {/* Trigger pill */}
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={config.label}
        className={`flex items-center gap-1 rounded-xl px-3 py-2 text-base font-semibold transition-colors duration-150 ${
          isOpen
            ? hasGradientText
              ? 'text-white'
              : 'bg-blue-100 text-blue-600'
            : 'text-gray-900'
        }`}
        onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>): void => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen((prev) => !prev);
          }
        }}
        style={
          isOpen && hasGradientText ? { background: AI_GRADIENT_BG } : undefined
        }
      >
        {/* Label */}
        {hasGradientText && !isOpen ? (
          <span className="flex items-center gap-1">
            {resolvedGradientIcon && (
              <img src={resolvedGradientIcon} alt="" width={9} height={9} />
            )}
            <span
              style={{
                background: AI_GRADIENT_BG,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {config.label}
            </span>
          </span>
        ) : hasGradientText && isOpen ? (
          <span className="flex items-center gap-1">
            {resolvedGradientIcon && (
              <img
                src="/assets/common/images/top-nav/starFourSidedGradientWhite.svg"
                alt=""
                width={9}
                height={9}
              />
            )}
            <span>{config.label}</span>
          </span>
        ) : (
          <span>{config.label}</span>
        )}

        {/* Chevron */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          style={{
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Spacer for mouse bridge */}
          <div
            className="absolute left-0 right-0 h-7"
            style={{ zIndex: 1055 }}
          />

          {/* Dropdown shell — inline zIndex so it stacks above the search
              typeahead (which uses z-50/1000 and lives later in DOM order).
              Tailwind JIT/arbitrary values aren't compiled in this repo, so
              the value is set inline rather than via a class. */}
          <div
            className="absolute left-0 top-full mt-7 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md"
            style={{ zIndex: 1060 }}
            role="menu"
          >
            {dropdown.type === 'two-column' && (
              <TwoColumnMenu
                links={dropdown.links}
                cta={dropdown.cta}
                categories={dropdown.categories}
              />
            )}
            {dropdown.type === 'single-column' && (
              <SingleColumnMenu links={dropdown.links} />
            )}
            {dropdown.type === 'layered' && (
              <LayeredMenu sections={dropdown.sections} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
